/**
 * Static content arrays for landing page sections.
 *
 * Icons are stored as component references (not JSX elements) so they can
 * be instantiated inside the React tree with proper context.
 */
import type { FluentIcon } from '@fluentui/react-icons';
import {
  BookFilled,
  BotFilled,
  BotSparkleFilled,
  BrainCircuitFilled,
  ChatFilled,
  CodeFilled,
  GlobeFilled,
  HandRightFilled,
  PeopleTeamFilled,
  PlugConnectedFilled,
  ShieldCheckmarkFilled,
} from '@fluentui/react-icons';

export interface TrustStat {
  readonly value: string;
  readonly label: string;
}

export interface FeatureData {
  readonly Icon: FluentIcon;
  readonly title: string;
  readonly desc: string;
}

export interface ScenarioStep {
  readonly title: string;
  readonly desc: string;
}

export interface EnterpriseCardData {
  readonly title: string;
  readonly items: readonly string[];
}

// ── Trust strip (right under hero) ───────────────────────────────────

export const TRUST_STATS: readonly TrustStat[] = [
  { value: '100K+', label: 'Customers on platform' },
  { value: '1,400+', label: 'Connectors' },
  { value: 'Trillions', label: 'Operations per month' },
];

// ── AI-native feature grid ───────────────────────────────────────────

export const FEATURES: readonly FeatureData[] = [
  {
    Icon: ChatFilled,
    title: 'Natural language development',
    desc: 'Describe what you need using plain words. Let AI build the workflow for you, whether for a single action or an end-to-end flow.',
  },
  {
    Icon: BotSparkleFilled,
    title: 'Agents and orchestration',
    desc: 'Build agents in Microsoft Foundry or with GitHub Copilot. Run them in a managed sandbox with PowerShell, skills, and repo integration. Orchestrate them inside your workflow.',
  },
  {
    Icon: PlugConnectedFilled,
    title: 'Tools and MCP servers',
    desc: 'Create tools from the 1,400+ connectors catalog, or expose any workflow as an MCP server. Connect to any MCP-compatible agent without writing code.',
  },
  {
    Icon: BookFilled,
    title: 'Knowledge as a service',
    desc: 'Add files to provide domain-specific information. The platform handles chunking, vectorizing, and retrieval. No pipeline to build. No vector store to manage. Just grounded answers.',
  },
  {
    Icon: BrainCircuitFilled,
    title: 'Any model, anywhere',
    desc: 'Plug in any AI model that fits the job — frontier, open-source, fine-tuned, or local. You\u2019re never locked in.',
  },
  {
    Icon: PeopleTeamFilled,
    title: 'Multi-agent ready',
    desc: 'Coordinate teams of specialized agents to handle complex business processes. Inspect the canvas surface to view every choice they make.',
  },
];

// ── Connector pill cloud ─────────────────────────────────────────────

export const CONNECTOR_PILLS: readonly string[] = [
  'Salesforce',
  'SAP',
  'ServiceNow',
  'Snowflake',
  'GitHub',
  'Slack',
  'Jira',
  'Postgres',
  'Microsoft 365',
  'Dynamics 365',
  'Workday',
  '+ 1,389 more',
];

// ── Scenario steps ───────────────────────────────────────────────────

export const SCENARIO_STEPS: readonly ScenarioStep[] = [
  {
    title: 'Trigger',
    desc: 'Store managers upload reports to a SharePoint folder. The workflow automatically detects them.',
  },
  {
    title: 'Extract',
    desc: 'The workflow parses each spreadsheet and normalizes data across stores.',
  },
  {
    title: 'Validate',
    desc: 'A validation agent checks each row using its equipped business-rule skills.',
  },
  {
    title: 'Act',
    desc: 'The workflow routes flagged rows to humans for review. Clean rows update the inventory system automatically.',
  },
];

// ── Enterprise grid ──────────────────────────────────────────────────

export const ENTERPRISE_CARDS: readonly EnterpriseCardData[] = [
  {
    title: 'Identity & access',
    items: [
      'Microsoft Entra ID / SSO / SAML',
      'Role-based access control',
      'Environment-scoped permissions',
      'Encrypted secret stores',
    ],
  },
  {
    title: 'Networking & isolation',
    items: [
      'Isolated compute per environment',
      'VNET integration',
      'Private endpoints',
      'Regional data residency',
    ],
  },
  {
    title: 'Observability & DevOps',
    items: [
      'Audit logs & log streaming',
      'Workflow run history & alerts',
      'Git-based source control',
      'Isolated environments & diffs',
    ],
  },
];

// ── Sign-in mock steps (Low-barrier section visual) ──────────────────

export const SIGNIN_STEPS: readonly string[] = [
  'Sign in with work or school account.',
  'Create an environment.',
  'Create from a template or start from blank.',
  'Build, test, and run your workflow.',
];

// ── HITL section bullets ─────────────────────────────────────────────

export const HITL_BULLETS: readonly string[] = [
  'Approval and rejection steps in any workflow',
  'Structured input and output contracts for AI steps',
  'Rule-based routing for sensitive decisions',
  'Audit trail of every human and AI action',
];

// ── Stack-viz pills (Managed-yet-enterprise) ─────────────────────────

export const STACK_PILLS: readonly string[] = [
  'Isolated compute per environment',
  'VNET integration',
  'Private endpoints',
  'Entra ID',
  'Encrypted secrets',
  'Regional residency',
  'Compliance & certifications',
];

// ── Icon stash (re-exported for the icons import list above) ─────────
// Re-export commonly-used icons in non-FeaturesGrid sections so consumers
// don't have to remember which file each one lives in.
export {
  BotFilled,
  CodeFilled,
  GlobeFilled,
  HandRightFilled,
  ShieldCheckmarkFilled,
};
